import axios from "axios";
import { globalError } from "./globalError";

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// ---------------------------------------------------------------------------
// Endpoint allowlist - only paths matching these prefixes hit the dreamteam
// backend. Everything else (NHL-only endpoints) is short-circuited with an
// empty stub so admin pages don't blow up while we incrementally wire them.
// ---------------------------------------------------------------------------
const ENABLED_PATH_PREFIXES = [
  "/admin/players",
  "/admin/teams",
  "/admin/squads",
  "/admin/reports",
  "/admin/matches",
  "/admin/match-simulator",
  "/admin/leagues",
  "/admin/gameweek-prize-draw",
  "/admin/weekly-gameweek-prize",
  "/admin/gameweek-transition",
  "/admin/area",
  "/admin/country",
  "/admin/opta",
  "/admin/midnite",
  "/admin/boosters",
  "/admin-auth",
  "/admin/users",
  "/admin/app-users",
  "/admin/matchday",
  "/admin/gameweek",
  "/admin/tournament-calendar",
  "/tournament-calendars",
  "/admin/transfers",
  "/admin/scoring-rules",
  "/admin/feature-flags",
  "/admin/app-settings",
  "/admin/delivery-records",
  "/fantasy-teams",
  "/health",
];

// Paths that are enabled by prefix above but don't exist yet — stub them
// to avoid 404 error popups until the backend implements them.
const STUB_EXACT_PATHS = [
  "/admin/users/stats",
  "/admin/marketplace/purchases/stats",
];

function getAdminAuthHeader() {
  const adminToken = localStorage.getItem("adminToken");
  return adminToken ? { authorization: `Bearer ${adminToken}` } : {};
}

function normaliseUrl(url) {
  // Strip any query string for matching
  const clean = url.split("?")[0];
  // Match against prefix table - admin code historically uses paths like
  // "/admin/teams/1/scoreables" or "/players" (relative to baseURL).
  return clean;
}

export function isEnabled(url) {
  const path = normaliseUrl(url);
  if (STUB_EXACT_PATHS.includes(path)) return false;
  return ENABLED_PATH_PREFIXES.some(
    (p) => path === p || path.startsWith(p + "/") || path === p + "/"
  );
}

// SAFE_STUB is a Proxy that mimics an empty array: native array methods
// (map, filter, length, iteration) work and return empty results, and any
// other property access returns ANOTHER SAFE_STUB. This means pages can
// freely chain `response.data.items.map(...)`, `response.transactions.length`,
// `response.x.y.z` etc. without crashing. The stub is truthy (so ternaries
// that gate "data loaded?" enter the rendered branch) but renders as empty.
function createSafeStub() {
  const target = [];
  return new Proxy(target, {
    get(t, prop) {
      // Real Array members: length, map, filter, forEach, slice, etc.
      if (prop in t) {
        const val = t[prop];
        return typeof val === "function" ? val.bind(t) : val;
      }
      // Iterators
      if (prop === Symbol.iterator) return t[Symbol.iterator].bind(t);
      if (typeof prop === "symbol") return undefined;
      // Don't impersonate a thenable - would break Promise resolution
      if (prop === "then" || prop === "catch" || prop === "finally")
        return undefined;
      // Common scalar fields that pages might check
      if (prop === "success") return true;
      if (prop === "total" || prop === "count" || prop === "totalCount")
        return 0;
      // Default: another stub (chainable for `response.x.y.z`)
      return createSafeStub();
    },
  });
}

function stubResolve(method, url) {
  console.log(
    `[api] STUB ${method} ${url} - endpoint disabled in dreamteam backend`
  );
  return Promise.resolve(createSafeStub());
}

// Global swallower for any rejection that did slip through (e.g. axios
// errors from a non-stubbed path that hit a 404). Keeps the console clean
// while we incrementally wire endpoints.
if (typeof window !== "undefined" && !window.__ecffStubHandlerInstalled) {
  window.__ecffStubHandlerInstalled = true;
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (reason && (reason.stubbed || reason?.response?.status === 404)) {
      event.preventDefault();
      console.log(
        "[api] swallowed unhandled rejection:",
        reason.message ?? reason
      );
    }
  });
}

apiClient.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    console.log("API Error Details:", {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data || {},
      message: error.message,
    });
    const status = error.response?.status;

    if (!error.response) {
      return Promise.reject({
        response: {
          data: {
            code: "NETWORK_ERROR",
            message:
              "Unable to connect to server. Please check your internet connection.",
          },
        },
      });
    }
    // A caller that reports its own errors (e.g. a batch loop that retries)
    // passes `suppressErrorToast: true` in the request options.
    if (status !== 401 && !error.config?.suppressErrorToast) {
      const msg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Unknown server error";
      globalError.show(`Error ${status ?? ""}`.trim(), msg);
    }

    if (status === 401) {
      const hadAdminToken = !!localStorage.getItem("adminToken");
      localStorage.removeItem("adminToken");
      localStorage.removeItem("user");
      if (hadAdminToken) window.location.href = "/";
    }

    return Promise.reject(error);
  }
);

export const get = async (url, options = {}) => {
  if (!isEnabled(url)) return stubResolve("GET", url);
  return apiClient.get(url, {
    ...options,
    headers: { ...getAdminAuthHeader(), ...options.headers },
  });
};

export const post = async (url, data, options = {}) => {
  if (!isEnabled(url)) return stubResolve("POST", url);
  return apiClient.post(url, data, {
    ...options,
    headers: { ...getAdminAuthHeader(), ...options.headers },
  });
};

export const put = async (url, data, options = {}) => {
  if (!isEnabled(url)) return stubResolve("PUT", url);
  return apiClient.put(url, data, {
    ...options,
    headers: { ...getAdminAuthHeader(), ...options.headers },
  });
};

export const patch = async (url, data, options = {}) => {
  if (!isEnabled(url)) return stubResolve("PATCH", url);
  return apiClient.patch(url, data, {
    ...options,
    headers: { ...getAdminAuthHeader(), ...options.headers },
  });
};

export const del = async (url, options = {}) => {
  if (!isEnabled(url)) return stubResolve("DELETE", url);
  return apiClient.delete(url, {
    ...options,
    headers: { ...getAdminAuthHeader(), ...options.headers },
  });
};

export const delWithBody = async (url, data, options = {}) => {
  if (!isEnabled(url)) return stubResolve("DELETE", url);
  console.log(`API Request [DELETE] ${url}:`, data);
  return apiClient.delete(url, {
    ...options,
    data,
    headers: { ...getAdminAuthHeader(), ...options.headers },
  });
};
